// INV-1: query options forward the QueryClient signal to the request and its retry owner.
import type {
  SubscriptionHandle,
  SubscriptionOptionsOptional,
  SubscriptionQueryOptions,
} from '../core/types'
import type { Tel } from '@w/tel'

type QueryFnContext = { signal?: AbortSignal; tel?: Tel }

type MutationOptionsOptional<TInput, TOutput> = Partial<{
  onMutate: (variables: TInput) => Promise<void> | void
  onSuccess: (
    data: TOutput,
    variables: TInput,
    mutation: unknown,
  ) => Promise<void> | void
  onError: (
    error: Error,
    variables: TInput,
    mutation: unknown,
  ) => Promise<void> | void
  onSettled: (
    data: TOutput | undefined,
    error: Error | undefined,
    variables: TInput,
    mutation: unknown,
  ) => Promise<void> | void
  retry: boolean | number | ((failureCount: number, error: Error) => boolean)
  retryDelay: number | ((failureCount: number, error: Error) => number)
  throwOnError: boolean
  meta: Record<string, unknown>
  invalidates: string[]
}>

type QueryOptions<TQuery> = TQuery extends (
  ...args: infer TArgs
) => Promise<infer TOutput>
  ? TArgs extends [input?: undefined, ...rest: unknown[]]
    ? (input?: undefined) => {
        queryKey: unknown[]
        queryFn: (ctx?: QueryFnContext) => Promise<TOutput>
      }
    : TArgs extends [input: infer TInput, ...rest: unknown[]]
      ? (input: TInput) => {
          queryKey: unknown[]
          queryFn: (ctx?: QueryFnContext) => Promise<TOutput>
        }
      : never
  : never

type MutationOptions<TMutate> = TMutate extends (
  ...args: infer TArgs
) => Promise<infer TOutput>
  ? TArgs extends [input?: undefined, ...rest: unknown[]]
    ? <TOpts extends MutationOptionsOptional<undefined, TOutput>>(
        options?: TOpts,
      ) => {
        mutationFn: (variables: undefined) => Promise<TOutput>
      } & TOpts
    : TArgs extends [input: infer TInput, ...rest: unknown[]]
      ? <TOpts extends MutationOptionsOptional<TInput, TOutput>>(
          options?: TOpts,
        ) => {
          mutationFn: (variables: TInput) => Promise<TOutput>
        } & TOpts
      : never
  : never

type SubscriptionOptions<TSubscribe> = TSubscribe extends (
  ...args: infer TArgs
) => SubscriptionHandle<infer TItem>
  ? TArgs extends [input?: undefined, ...rest: unknown[]]
    ? (
        input?: undefined,
        options?: SubscriptionOptionsOptional<TItem>,
      ) => SubscriptionQueryOptions<TItem>
    : TArgs extends [input: infer TInput, ...rest: unknown[]]
      ? (
          input: TInput,
          options?: SubscriptionOptionsOptional<TItem>,
        ) => SubscriptionQueryOptions<TItem>
      : never
  : never

export type CreateOptionsProxy<TClient> = {
  [K in keyof TClient as K extends 'withTel' ? never : K]: TClient[K] extends {
    query: infer TQuery
    mutate: infer TMutate
    subscribe: infer TSubscribe
  }
    ? {
        queryOptions: QueryOptions<TQuery>
        mutationOptions: MutationOptions<TMutate>
        subscriptionOptions: SubscriptionOptions<TSubscribe>
      }
    : CreateOptionsProxy<TClient[K]>
}

export interface RpcOptionsConfig<TClient> {
  client: TClient
  // oxlint-disable-next-line typescript/no-explicit-any -- external query client type
  queryClient: any
}

export function createRpcOptionsProxy<TClient>(
  config: RpcOptionsConfig<TClient>,
): CreateOptionsProxy<TClient> {
  // oxlint-disable-next-line typescript/no-explicit-any -- recursive proxy builder returns dynamic type
  const buildProxy = (clientNode: any, basePath: string[] = []): any => {
    return new Proxy(
      {},
      {
        get(_target, prop: string) {
          const currentPath = [...basePath, prop]
          const procedureOrRouter = clientNode[prop]

          return new Proxy(
            {},
            {
              get(_innerTarget, method: string) {
                if (method === 'queryOptions') {
                  // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC input type
                  return (input?: any) => {
                    return {
                      queryKey: [...currentPath, input],
                      queryFn: (qctx?: QueryFnContext) =>
                        procedureOrRouter.query(input, { tel: qctx?.tel, signal: qctx?.signal }),
                    }
                  }
                }

                if (method === 'subscriptionOptions') {
                  // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC input/item types
                  return (input?: any, options?: any) => ({
                    queryKey: [...currentPath, input],
                    queryFn: async (qctx?: QueryFnContext) => {
                      // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC item type
                      const items: any[] = []
                      const subscription = procedureOrRouter.subscribe(
                        input,
                        undefined,
                        { tel: qctx?.tel },
                      )
                      qctx?.signal?.addEventListener('abort', () => {
                        subscription.unsubscribe()
                      })

                      for await (const item of subscription) {
                        items.push(item)
                        options?.onData?.(item, items)
                      }

                      return items
                    },
                    ...options,
                  })
                }

                if (method === 'mutationOptions') {
                  // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC mutation options type
                  return (options?: any) => {
                    return {
                      // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC variables type
                      mutationFn: (variables: any) =>
                        procedureOrRouter.mutate(variables),
                      ...options,
                    }
                  }
                }

                // Assume it's a nested router - use method (inner prop) not prop (outer)
                return buildProxy(procedureOrRouter, currentPath)[method]
              },
            },
          )
        },
      },
    )
  }

  return buildProxy(config.client)
}
